import * as React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Subtype, SubTypeResponse } from '../../types/ServiceAccordion.type';
import { z } from 'zod';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';
import { apiPost, apiPut } from '../../utils/apiClient';

type SubTypeDialogProps = {
  open: boolean;
  handleClose: () => void;
  subtype: Subtype | null;
  serviceTypeId: string;
  fetchServiceTypes: () => void;
};

const SubtypeInput = z.object({
  id: z.number().int().optional(),
  name: z
    .string()
    .min(1, 'Service type name is required')
    .refine((val) => val.length === 0 || val.trim().length > 0, {
      message: 'Service type name cannot be just whitespace',
    }),
  serviceTypeId: z.number().int().optional(),
});
type SubtypeInputType = z.infer<typeof SubtypeInput>;

const SubTypeDialog = ({
  open,
  handleClose,
  subtype,
  serviceTypeId,
  fetchServiceTypes,
}: SubTypeDialogProps) => {
  const [formError, setFormError] = React.useState<string | null>(null);

  const handleConfirm = async (data: SubtypeInputType) => {
    try {
      if (subtype) {
        // Update existing service type
        const updateRes = await apiPut<SubTypeResponse, SubtypeInputType>(
          '/service/subtype/update',
          data,
        );
        notifyAPIClient(updateRes.message);
      } else {
        // Create new service type
        const createRes = await apiPost<SubTypeResponse, SubtypeInputType>(
          '/service/subtype/create',
          {
            ...data,
            serviceTypeId: Number(serviceTypeId),
          },
        );
        notifyAPIClient(createRes.message);
      }
      fetchServiceTypes();
      handleClose();
    } catch (error) {
      notifyFailure();
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const data = SubtypeInput.parse({
        id: subtype?.id || 0,
        name: formData.get('name'),
      });
      setFormError(null);
      handleConfirm(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        setFormError(error.errors[0].message);
      } else {
        console.error(error);
      }
    }
  };

  return (
    <Dialog fullWidth open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add new service type</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            name="name"
            className="required-field"
            label="Service Type Name"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={subtype?.name || ''}
            error={Boolean(formError)}
            helperText={formError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit">Confirm</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SubTypeDialog;
